// =============================================================================
// CreditNotesPage — list view (owner-only feature)
// =============================================================================
// Browse all issued credit notes with status tabs + a "+ New credit note"
// action that opens the issue flow. B2B customers are visually distinguished
// because they're the bulk of CN usage at Kalachand.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { EmptyState } from '../../../components/EmptyState';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { cn } from '../../../lib/cn';

type CnStatus = 'DRAFT' | 'ISSUED' | 'APPLIED' | 'VOID';

interface CreditNoteRow {
  id: string;
  number: string;
  status: CnStatus;
  reason: string;
  vatExempt: boolean;
  total: string;
  appliedTotal: string;
  remainingTotal: string;
  issuedAt: string | null;
  createdAt: string;
  customer: {
    id: string;
    name: string;
    companyName: string | null;
    type: 'individual' | 'company';
  };
  invoice: { id: string; number: string } | null;
  createdBy: { id: string; name: string };
}

type Tab = 'all' | 'issued' | 'applied' | 'void';

const TABS: { key: Tab; label: string; matches: (s: CnStatus) => boolean }[] = [
  { key: 'all', label: 'All', matches: () => true },
  { key: 'issued', label: 'Open', matches: (s) => s === 'ISSUED' },
  { key: 'applied', label: 'Applied', matches: (s) => s === 'APPLIED' },
  { key: 'void', label: 'Void', matches: (s) => s === 'VOID' },
];

function statusTone(s: CnStatus): 'positive' | 'warning' | 'negative' | 'neutral' {
  switch (s) {
    case 'APPLIED':
      return 'positive';
    case 'ISSUED':
      return 'warning';
    case 'VOID':
      return 'negative';
    default:
      return 'neutral';
  }
}

function reasonLabel(r: string): string {
  switch (r) {
    case 'RETURN':
      return 'Return';
    case 'PRICE_CORRECTION':
      return 'Price correction';
    case 'DISCOUNT_GRANTED':
      return 'Discount given';
    case 'CANCELLATION':
      return 'Invoice cancelled';
    case 'WRITE_OFF':
      return 'Bad debt';
    case 'GOODWILL':
      return 'Goodwill';
    default:
      return r;
  }
}

export function CreditNotesPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('all');

  const list = useQuery({
    queryKey: ['credit-notes'],
    queryFn: () => apiGet<CreditNoteRow[]>('/credit-notes'),
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    const def = TABS.find((t) => t.key === tab)!;
    return (list.data ?? []).filter((c) => def.matches(c.status));
  }, [list.data, tab]);

  const counts = useMemo(() => {
    const out: Record<Tab, number> = { all: 0, issued: 0, applied: 0, void: 0 };
    for (const c of list.data ?? []) {
      out.all += 1;
      const k =
        c.status === 'ISSUED'
          ? 'issued'
          : c.status === 'APPLIED'
            ? 'applied'
            : c.status === 'VOID'
              ? 'void'
              : 'all';
      if (k !== 'all') out[k] += 1;
    }
    return out;
  }, [list.data]);

  const openTotal = useMemo(() => {
    return (list.data ?? [])
      .filter((c) => c.status === 'ISSUED')
      .reduce((sum, c) => sum + Number(c.remainingTotal), 0);
  }, [list.data]);

  const HEADER_ACTIONS = (
    <Link to="/credits/new">
      <Button>+ Issue credit note</Button>
    </Link>
  );

  return (
    <PageScaffold
      title="Credit Notes"
      subtitle={
        <>
          Reduce a customer's AR after invoice issue — returns, price corrections, write-offs.
          <span className="text-accent ml-1 font-medium">Owner-only.</span>
        </>
      }
      actions={HEADER_ACTIONS}
      scroll={false}
    >
      {/* Outstanding open-credit hero */}
      {openTotal > 0 && (
        <Card bodyClassName="px-6 py-4" className="shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-3">
                Open credit owed to customers
              </div>
              <div className="text-2xl kc-serif text-accent mt-1">
                <Money value={openTotal} />
              </div>
              <div className="text-xs text-ink-3 mt-0.5">
                {counts.issued} note{counts.issued === 1 ? '' : 's'} not fully applied
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card bodyClassName="p-4" className="shrink-0">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                tab === t.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
              )}
            >
              <span>{t.label}</span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded',
                  tab === t.key ? 'bg-white/20 text-white' : 'bg-surface text-ink-3',
                )}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No credit notes here"
            description={
              tab === 'all'
                ? "You haven't issued any credit notes yet."
                : `Nothing in the "${TABS.find((t) => t.key === tab)?.label}" bucket.`
            }
            action={
              tab === 'all' ? (
                <Link to="/credits/new">
                  <Button>+ Issue credit note</Button>
                </Link>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Credit Note</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Against invoice</th>
                <th className="px-6 py-2 font-medium">Reason</th>
                <th className="px-6 py-2 font-medium text-right">Total</th>
                <th className="px-6 py-2 font-medium text-right">Remaining</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const isCompany = c.customer.type === 'company';
                const display = isCompany
                  ? (c.customer.companyName ?? c.customer.name)
                  : c.customer.name;
                const applied = Number(c.appliedTotal);
                const total = Number(c.total) || 1;
                const pct = Math.min(100, Math.round((applied / total) * 100));
                return (
                  <tr key={c.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3 align-top">
                      <Link
                        to={`/credits/${c.id}`}
                        className="kc-mono text-xs text-ink hover:text-accent"
                      >
                        {c.number}
                      </Link>
                      <div className="text-[10px] text-ink-3 mt-0.5">
                        {new Date(c.issuedAt ?? c.createdAt).toLocaleDateString('en-MU', {
                          day: 'numeric',
                          month: 'short',
                        })}
                        {c.vatExempt && <span className="ml-2 uppercase">· VAT-exempt</span>}
                      </div>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-ink">{display}</span>
                        {isCompany && (
                          <StatusPill tone="accent" className="text-[10px]">
                            B2B
                          </StatusPill>
                        )}
                      </div>
                      {isCompany && c.customer.name !== display && (
                        <div className="text-[10px] text-ink-3 mt-0.5">Attn: {c.customer.name}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 align-top">
                      {c.invoice ? (
                        <Link
                          to={`/orders?invoice=${c.invoice.number}`}
                          className="kc-mono text-xs text-ink-2 hover:text-accent"
                        >
                          {c.invoice.number}
                        </Link>
                      ) : (
                        <span className="text-xs text-ink-3 italic">none</span>
                      )}
                    </td>
                    <td className="px-6 py-3 align-top text-xs text-ink-2">
                      {reasonLabel(c.reason)}
                    </td>
                    <td className="px-6 py-3 text-right align-top kc-mono">
                      <Money value={c.total} className="text-ink" />
                    </td>
                    <td className="px-6 py-3 text-right align-top">
                      <div
                        className={cn(
                          'kc-mono',
                          applied >= total ? 'text-ink-3 line-through' : 'text-accent',
                        )}
                      >
                        <Money value={c.remainingTotal} />
                      </div>
                      {c.status === 'ISSUED' && pct > 0 && (
                        <div className="text-[10px] text-ink-3 mt-0.5">{pct}% applied</div>
                      )}
                    </td>
                    <td className="px-6 py-3 align-top">
                      <StatusPill tone={statusTone(c.status)}>{c.status.toLowerCase()}</StatusPill>
                    </td>
                    <td className="px-6 py-3 align-top text-right">
                      <Link to={`/credits/${c.id}`}>
                        <Button size="sm" variant="ghost">
                          Open
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}
